import React from 'react';
import type { Key } from 'react';
import { useSelector } from 'react-redux';
import { Field, Select } from '@milanote/roma';
import type { CheckboxProps } from '@milanote/roma';
import { FormatOptions } from '../../../../common/table/CellTypeConstants';
import { getCurrencyDropdownLabel, getCurrencyList } from '../../../utils/currencyUtils';
import { getCurrencyDisplayOptions } from '../../../../common/table/utils/tableCellFormattingUtils';
import { userCurrencyDisplayLocaleSelector } from '../../../user/currentUserSelector';
import type { CellTypeFormatChanges, CurrencyTypeObject } from '../../../../common/table/TableTypes';

import { useTranslator } from '../../../components/i18n/useTranslator';
import { selectIsMobileAppMode } from '../../../platform/platformSelector';

type Props = {
    cellTypeObject: CurrencyTypeObject;
    updateCellTypeFormat: (cellTypeFormatChanges: CellTypeFormatChanges) => void;
    locale: string;
    formSize: CheckboxProps['size'];
};

const CurrencyFormatOptions: React.FC<Props> = (props) => {
    const { cellTypeObject, updateCellTypeFormat, locale, formSize } = props;
    const { currency, currencyDisplay } = cellTypeObject;

    const translate = useTranslator('popup', 'table.format');

    const currencyDisplayLocale = useSelector(userCurrencyDisplayLocaleSelector);
    const isMobileAppMode = useSelector(selectIsMobileAppMode);
    const currencyItems = getCurrencyList(currencyDisplayLocale).map(({ id, label, currencyCode }) => ({
        id,
        label: getCurrencyDropdownLabel({ label, currencyCode }),
    }));

    const currencyDisplayItems = currency
        ? getCurrencyDisplayOptions(currency, locale).map(({ id, label }) => ({ id, label }))
        : [];

    const updateCurrency = (currency: Key | null): void => {
        if (currency == null) return;

        updateCellTypeFormat({ [FormatOptions.CURRENCY]: String(currency) });
    };

    const updateCurrencyDisplay = (currencyDisplay: Key | null): void => {
        if (currencyDisplay == null) return;

        updateCellTypeFormat({ [FormatOptions.CURRENCY_DISPLAY]: String(currencyDisplay) });
    };

    return (
        <div className="CurrencyFormatOptions celltype-format-section">
            <Field.Label className="celltype-format-title" size={formSize}>
                {translate('currency')}
            </Field.Label>
            <div className="celltype-format-inputs">
                <div className="celltype-format-input">
                    <Select
                        size={formSize}
                        value={currency || ''}
                        onChange={updateCurrency}
                        items={currencyItems}
                        native={isMobileAppMode}
                    >
                        {(item) => <Select.Item {...item} />}
                    </Select>
                </div>
                {currency && (
                    <div className="celltype-format-input">
                        <Select
                            size={formSize}
                            value={currencyDisplay || ''}
                            onChange={updateCurrencyDisplay}
                            items={currencyDisplayItems}
                            native={isMobileAppMode}
                        >
                            {(item) => <Select.Item {...item} />}
                        </Select>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CurrencyFormatOptions;
